import { useEffect, useRef, useState } from "react";
import type { LeadSource } from "../../api/types";
import css from "./SourcePicker.module.css";

/**
 * Custom dropdown for the booking-modal source field. Per screen-03 эталон
 * (`.cyber-pop` with `.cyber-opt` rows + `.cyber-add` row + inline input):
 *
 *  - Gradient border (green→purple)
 *  - List of active lead_sources
 *  - Hover: green chevron `›` slides in, row tinted
 *  - Bottom row "Добавить источник" with dashed-top + green `+` glyph
 *  - Click "+" → swaps to inline input + check button; Enter or click commits.
 *
 * Persistence of new sources is a TODO — the эталон's mockup appends to a
 * static array. Here we surface the new value via `onCreate` so the parent
 * can POST to `/api/lead_sources` and refresh the list.
 */
export interface SourcePickerProps {
  /** Currently selected source name. */
  value: string;
  /** Active lead sources to choose from. */
  sources: LeadSource[];
  onPick: (name: string) => void;
  onClose: () => void;
  /**
   * Optional callback to create a new source server-side. If omitted, the
   * "Добавить источник" row is hidden.
   */
  onCreate?: (name: string) => Promise<void> | void;
}

export function SourcePicker({ value, sources, onPick, onClose, onCreate }: SourcePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const newInputRef = useRef<HTMLInputElement>(null);

  /* Outside-click + Esc close. */
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (adding) newInputRef.current?.focus();
  }, [adding]);

  const commitNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed || !onCreate) {
      setAdding(false);
      setNewName("");
      return;
    }
    try {
      await onCreate(trimmed);
      onPick(trimmed);
      onClose();
    } catch {
      // parent surfaces the error via toast; keep the input open
    }
  };

  const active = sources.filter((s) => s.active);

  return (
    <div ref={rootRef} className={css.pop} role="dialog" aria-label="Выбор источника">
      <div className={css.list}>
        {active.map((s) => {
          const selected = s.name === value;
          return (
            <button
              key={s.id}
              type="button"
              className={`${css.opt} ${selected ? css.selected : ""}`}
              onClick={() => {
                onPick(s.name);
                onClose();
              }}
            >
              <span className={css.chev} aria-hidden="true">›</span>
              <span className={css.optName}>{s.name}</span>
            </button>
          );
        })}

        {onCreate ? (
          adding ? (
            <div className={css.addRow}>
              <input
                ref={newInputRef}
                type="text"
                className={css.addInput}
                placeholder="Новый источник…"
                value={newName}
                maxLength={50}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void commitNew();
                  }
                }}
                aria-label="Название нового источника"
                autoComplete="off"
              />
              <button
                type="button"
                className={css.addConfirm}
                onClick={commitNew}
                disabled={!newName.trim()}
                aria-label="Сохранить источник"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 6 L5 9 L10 3" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={`${css.opt} ${css.optAdd}`}
              onClick={() => setAdding(true)}
            >
              <span className={css.addGlyph} aria-hidden="true">+</span>
              <span className={css.optName}>Добавить источник</span>
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}
